import { useEffect, useState } from "react";
import { Link } from "react-router";
import { homeCardAllowed, profileFor } from "@wato/business-types";
import { useI18n } from "@wato/i18n/react";
import { useApi } from "../../api/context";
import type { HomeKpiView } from "../../api/analytics-types";
import { formatAmount } from "../billing/format";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { HOME_CARDS, type HomeCardDetail } from "./home-cards";

/**
 * v07: the home cards of the workspace's business-type profile (`profileFor(type).home`), each with
 * a number when its module has one (from `GET .../home/kpis`), otherwise an empty state, and a link
 * to the page. Never shows an invented number.
 */
export function ProfileHome({ workspaceId }: { readonly workspaceId: string }) {
  const { t, locale } = useI18n();
  const api = useApi();
  const { workspace } = useWorkspace();
  const profile = profileFor(workspace.businessType);
  const [kpis, setKpis] = useState<readonly HomeKpiView[] | null | "failed">(null);

  useEffect(() => {
    let live = true;
    api.getHomeKpis(workspaceId).then(
      (k) => live && setKpis(k),
      () => live && setKpis("failed"),
    );
    return () => {
      live = false;
    };
  }, [api, workspaceId]);

  const fmtN = (n: number) =>
    new Intl.NumberFormat(locale === "ar" ? "ar-u-nu-latn" : "en").format(n);
  const fmtMoney = (m: readonly { currency: string; amountMinor: string }[]) =>
    m.map((x) => formatAmount(x.amountMinor, x.currency, locale)).join(" · ");
  const fmtDuration = (sec: number) =>
    sec < 60
      ? t("dashboard.home.profile.detail.durationSeconds", { count: Math.max(1, Math.round(sec)) })
      : sec < 3600
        ? t("dashboard.home.profile.detail.durationMinutes", { count: Math.round(sec / 60) })
        : t("dashboard.home.profile.detail.durationHours", {
            count: Math.round(sec / 360) / 10,
          });
  const detailParams = (d: HomeCardDetail) =>
    d.kind === "count"
      ? { count: fmtN(d.n) }
      : d.kind === "money"
        ? { amount: fmtMoney(d.money) }
        : d.kind === "percent"
          ? { percent: `${fmtN(d.basisPoints / 100)}%` }
          : { duration: fmtDuration(d.seconds) };

  const data = Array.isArray(kpis) ? kpis : [];
  return (
    <section aria-label={t("dashboard.home.profile.title")} className="flex flex-col gap-3">
      <h3 className="text-lg font-semibold text-fg">{t("dashboard.home.profile.title")}</h3>
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(11rem,1fr))]">
        {profile.home
          .filter((id) => homeCardAllowed(workspace.modules, id))
          .map((id) => {
            const def = HOME_CARDS[id];
            const value = def.value?.(data);
            return (
              <div key={id} className="rounded-2xl border border-app-line bg-app-surface p-4">
                <p className="text-xs text-fg-muted">{t(def.title)}</p>
                {value ? (
                  <>
                    {value.main && (
                      <p
                        data-testid={`home-card-${id}`}
                        className="mt-1 text-xl font-semibold text-fg"
                      >
                        {value.main.kind === "count"
                          ? fmtN(value.main.n)
                          : fmtMoney(value.main.money)}
                      </p>
                    )}
                    {value.details.map((d) => (
                      <p
                        key={d.key}
                        data-testid={`home-card-${id}-${d.key.split(".").pop()}`}
                        className="mt-1 text-sm text-fg-muted"
                      >
                        {t(d.key, detailParams(d))}
                        {d.kind === "count" && d.to && (
                          <>
                            {" "}
                            <Link to={`/w/${workspaceId}/${d.to}`} className="underline">
                              {t("dashboard.home.profile.open")}
                            </Link>
                          </>
                        )}
                      </p>
                    ))}
                  </>
                ) : (
                  <p data-testid={`home-card-${id}-empty`} className="mt-1 text-sm text-fg-muted">
                    {t(
                      kpis === "failed"
                        ? "dashboard.home.profile.unavailable"
                        : "dashboard.home.profile.empty",
                    )}
                  </p>
                )}
                <Link to={`/w/${workspaceId}/${def.to}`} className="text-xs underline">
                  {t("dashboard.home.profile.open")}
                </Link>
              </div>
            );
          })}
      </div>
    </section>
  );
}
